/**
 * The one SSH-port normaliser, shared by both processes.
 *
 * Lives next to `probeConnectionId.ts` for the same reason: main and renderer
 * must agree, byte for byte, about what a port *is* — and here they must agree
 * with ssh2 as well.
 *
 * ## Why this exists (Task 11d round 2, P1)
 *
 * The identity tag that binds a stored SSH secret to its bastion hashes the
 * port. The first version of that normaliser mapped anything that was not a
 * `number` to 22 — while the dial handed `config.port` straight to ssh2, which
 * happily accepts `"2222"`. A config with `port: '2222'` therefore hashed as
 * port 22, matched a tag written for `host:22`, released that secret, and then
 * connected to `host:2222`. Coercing in one place and refusing what does not
 * coerce closes that: a numeric string is the number it spells, and anything
 * else is not a port at all.
 *
 * Three call sites must use this and nothing else:
 *   • the v2 identity + the legacy corroboration (`sshSecretsSave.ts`);
 *   • the tunnel-cache identity and the dial rejection (`sshTunnelManager.ts`);
 *   • the dial projection (`dialProjection.ts`), which feeds both
 *     `dialFingerprint` and the pool-reset predicate.
 */

/** What ssh2 uses when a config carries no port at all. */
export const DEFAULT_SSH_PORT = 22;

/**
 * Coerce a value to an SSH port.
 *
 * Accepts a number, or a string that spells one — `2222` and `' 2222 '` are the
 * same port, because that is how ssh2 reads them. Returns `undefined` for
 * anything that is not a whole port in 1..65535: an absent value, a float, a
 * negative, an out-of-range number, `'22abc'`, `'0x16'`, `''`, `NaN`,
 * `Infinity`, a boolean, an object.
 *
 * `undefined` is deliberately NOT "the default". A caller that wants the
 * default for an ABSENT port asks for it explicitly (`sshPortIdentityKey`, or
 * `?? DEFAULT_SSH_PORT` after checking the value was absent); a caller
 * validating user input refuses `undefined` outright. Folding the two together
 * is exactly the bug above.
 */
export function normalizeSshPort(value: unknown): number | undefined {
  let candidate: number;
  if (typeof value === 'number') {
    candidate = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    // Digits only: `Number('0x16')` is 22 and `Number('1e3')` is 1000, neither
    // of which is how anyone writes a port — and `Number('')` is 0.
    if (!/^\d+$/.test(trimmed)) return undefined;
    candidate = Number(trimmed);
  } else {
    return undefined;
  }
  if (!Number.isInteger(candidate) || candidate < 1 || candidate > 65535) return undefined;
  return candidate;
}

/** True when the value carries no port at all, as opposed to a bad one. */
export function isAbsentSshPort(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}

/**
 * The stable string a port contributes to an identity hash or a dial
 * fingerprint. Three outcomes, and they never collide:
 *   • absent  → `'22'`, the protocol default, so a record that omits `port` and
 *     one that stores an explicit `22` keep producing the same tag (this is
 *     what keeps already-written v2 tags valid);
 *   • valid   → the number it spells, so `2222` and `'2222'` agree;
 *   • invalid → `'invalid'`, which is not any port. A malformed port must never
 *     hash like 22 — that is the bypass this module exists to close — even
 *     though the dial path also refuses it outright before getting here.
 */
export function sshPortIdentityKey(value: unknown): string {
  if (isAbsentSshPort(value)) return String(DEFAULT_SSH_PORT);
  const port = normalizeSshPort(value);
  return port === undefined ? 'invalid' : String(port);
}
